import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronRight, 
  Search, 
  MoreHorizontal, 
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  Trash2,
  UserX
} from 'lucide-react';

const SubscriptionUsersManagement = () => {
  const [activeTab, setActiveTab] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mock data for subscription users
  const subscriptionUsers = [
    {
      id: 1,
      name: 'Marvin McKinney',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@marvinmc123',
      gender: 'Female',
      profileStatus: 'Verified',
      email: 'marvinmckinney@gmail.com',
      status: 'Active'
    },
    {
      id: 2,
      name: 'Darlene Robertson',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b332c1c7?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@darlene98',
      gender: 'Male',
      profileStatus: 'Not Verified',
      email: 'darlene98@gmail.com',
      status: 'Active'
    },
    {
      id: 3,
      name: 'Kristin Watson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@kristinwat78',
      gender: 'Male',
      profileStatus: 'Verified',
      email: 'kristinwat78@gmail.com',
      status: 'Active'
    },
    {
      id: 4,
      name: 'Theresa Webb',
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@theresawebb',
      gender: 'Female',
      profileStatus: 'Not Verified',
      email: 'theresawebb@gmail.com',
      status: 'Cancelled'
    },
    {
      id: 5,
      name: 'Ronald Richards',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@ronaldrich56',
      gender: 'Male',
      profileStatus: 'Verified',
      email: 'ronaldrich56@gmail.com',
      status: 'Active'
    },
    {
      id: 6,
      name: 'Kathryn Murphy',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@devonlane34',
      gender: 'Female',
      profileStatus: 'Not Verified',
      email: 'devonlane34@gmail.com',
      status: 'Cancelled'
    },
    {
      id: 7,
      name: 'Albert Flores',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@kathrynmurphy23',
      gender: 'Male',
      profileStatus: 'Verified',
      email: 'kathrynmurphy23@gmail.com',
      status: 'Active'
    },
    {
      id: 8,
      name: 'Kathryn Murphy',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=40&h=40&fit=crop&crop=face',
      joinedOn: 'May12, 2025 10:04 AM',
      username: '@albertfl89',
      gender: 'Female',
      profileStatus: 'Not Verified',
      email: 'albertfl89@gmail.com',
      status: 'Cancelled'
    }
  ];

  const filteredUsers = subscriptionUsers.filter(user => {
    const matchesTab = user.status === activeTab;
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      const allUserIds = new Set(filteredUsers.map(user => user.id));
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId, checked) => {
    const newSelection = new Set(selectedUsers);
    if (checked) {
      newSelection.add(userId);
    } else {
      newSelection.delete(userId);
    }
    setSelectedUsers(newSelection);
  };

  const toggleDropdown = (userId) => {
    setOpenDropdown(openDropdown === userId ? null : userId);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setOpenDropdown(null);
    setShowDeleteModal(true);
  };

  const handleCancelSubscription = (user) => {
    setSelectedUser(user);
    setOpenDropdown(null);
    setShowCancelModal(true);
  };

  const confirmDelete = () => {
    console.log('Deleting user:', selectedUser);
    setShowDeleteModal(false);
    setSelectedUser(null);
    // Add delete logic here
  };

  const confirmCancelSubscription = () => {
    console.log('Cancelling subscription for user:', selectedUser);
    setShowCancelModal(false);
    setSelectedUser(null);
    // Add cancel subscription logic here
  };

  const handleAddUser = () => {
    console.log('Add user clicked');
    // Add user logic here
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>Dashboard</span>
            <ChevronRight className="w-4 h-4" />
            <span>Subscription Plans</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">Users in 1 Month Plan</span>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 px-1 py-4">
          <div className="max-w-7xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Subscription Users</h1>
                <p className="text-gray-600">All the users with Subscription Plan are listed here.</p>
              </div>
              {/* <button
                onClick={handleAddUser}
                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium">
                Add
              </button> */}
            </div>

            {/* Tabs */}
            <div className="flex space-x-8 mb-6">
              {['Active', 'Cancelled'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2 border-b-2 font-medium transition-colors ${
                    activeTab === tab
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search users by name or username"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-12 px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined on
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profile Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="text-sm font-medium text-gray-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.joinedOn}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.gender}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.profileStatus === 'Verified'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.profileStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        <div className="relative" ref={openDropdown === user.id ? dropdownRef : null}>
                          <button
                            onClick={() => toggleDropdown(user.id)}
                            className="hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                          >
                            <MoreHorizontal className="w-5 h-5" />
                          </button>
                          
                          {openDropdown === user.id && (
                            <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                              <div className="py-1">
                                <button
                                  onClick={() => handleCancelSubscription(user)}
                                  className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                                >
                                  <UserX className="w-4 h-4" />
                                  <span>Cancel Subscription</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Delete User</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {selectedUsers.size} of {filteredUsers.length} row(s) selected.
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Rows per page</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Page 1 of 10</span>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Delete User</h2>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This will permanently remove the user and all associated data from the system.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <UserX className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cancel Subscription</h2>
                <p className="text-sm text-gray-600">This will end the user's subscription</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to cancel the subscription for <strong>{selectedUser?.name}</strong>? The user will lose access to premium features immediately.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCancelSubscription}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionUsersManagement;